import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { DateTime } from "luxon"
import Card from "../../../../components/atoms/Card.atom"
import { getTeamMemberVacationRequests } from "../../../../services/vacation.service"
import { capitalizeFirstLetter } from "../../../../utils/strings.utils"
import VacationRequestCard, {
  VacationRequestCardSkeleton,
} from "../../components/VacationRequestCard.compound"
import Badge from "../../../../components/atoms/Badge.atom"
import {
  getVacationRequestPriority,
  getVacationState,
  isVacationRequestApproved,
  isVacationRequestPending,
} from "../../../../utils/vacation.utils"
import {
  TEAM_VACATION_REQUESTS_FILTER_OPTIONS,
  VACATION_STATES,
  MY_VACATION_STATES,
} from "../../../../consts/modules/vacations.consts"
import TeamMemberVacationsHeader from "./TeamMemberVacationsHeader.controller"
import useFloatingShowMore from "../../../../hooks/useFloatingShowMore.hook"
import FloatingButton from "../../components/FloatingButton.molecule"
import TeamMemberVacationRequestsContextMenu from "../../components/TeamMemberVacationRequestsContextMenu"

const TeamMemberVacationsRequests = ({
  selectedPeriod = [],
  setVisibleModal = () => {},
  selectedVacationRequest = null,
  setSelectedVacationRequest = () => {},
  refresh,
  triggerRefresh,
}) => {
  const [vacationRequests, setVacationRequests] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState(
    TEAM_VACATION_REQUESTS_FILTER_OPTIONS
  )
  const requestElementRef = useRef(null)
  const listContainerRef = useRef(null)
  const [submittingCancel, setSubmittingCancel] = useState(false)
  const showSeeMore = useFloatingShowMore(listContainerRef, [
    vacationRequests,
    selectedFilter,
  ])
  const cmRef = useRef()
  useEffect(() => {
    fetchData()
  }, [selectedPeriod, refresh])

  const fetchData = async () => {
    setLoading(true)
    setVacationRequests([])
    try {
      const response = await getTeamMemberVacationRequests(
        selectedPeriod[0].toISODate(),
        selectedPeriod[1].toISODate()
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      setVacationRequests(response.result)
    } catch (error) {
      console.error("Error fetching vacation requests:", error)
      setVacationRequests([])
    } finally {
      setLoading(false)
    }
  }

  const getFilteredRequests = (vacationRequests) => {
    return vacationRequests
      .filter((item) => {
        if (
          !selectedFilter.rejected_requests &&
          getVacationState(item) === VACATION_STATES.REJECTED
        ) {
          return false
        }
        if (
          !selectedFilter.past_requests &&
          getVacationState(item) === VACATION_STATES.BLOCKED
        ) {
          return false
        }
        return true
      })
      .map((item) => ({
        ...item,
        priority: getVacationRequestPriority(item),
      }))
  }
  const groupRequestsByMonth = (vacationRequests) => {
    return vacationRequests.reduce((acc, request) => {
      const date = new Date(request.startDate)
      const monthName = `${date.toLocaleString("es-ES", { month: "long" })} ${date.getFullYear()}`
      if (!acc[monthName]) {
        acc[monthName] = []
      }
      acc[monthName].push(request)
      return acc
    }, {})
  }
  const renderVacationRequests = () => {
    const filteredRequests = getFilteredRequests(vacationRequests)

    const sortedRequests = filteredRequests.sort(
      (a, b) => a.priority - b.priority || a.startDate - b.startDate
    )
    const groups = groupRequestsByMonth(sortedRequests)
    const sortedMonthKeys = Object.keys(groups).sort((m1, m2) => {
      const date1 = DateTime.fromFormat(m1, "LLLL yyyy", { locale: "es" })
      const date2 = DateTime.fromFormat(m2, "LLLL yyyy", { locale: "es" })
      return date1.toMillis() - date2.toMillis()
    })
    return sortedMonthKeys.map((month) => (
      <div key={month}>
        <div className="requests-list-subheader flex py-2">
          <span
            className="font-bold"
            ref={
              month === DateTime.now().toFormat("MMMM") ?
                requestElementRef
              : null
            }
          >
            {capitalizeFirstLetter(month)}
          </span>
        </div>
        {groups[month].map((vacationRequest, index) => {
          const teamMember = vacationRequest.teamMember
          return (
            <VacationRequestCard
              key={vacationRequest.id || index}
              vacationRequest={vacationRequest}
              teamMember={teamMember}
              onClick={() => {
                setVisibleModal(MY_VACATION_STATES.QUERY)
                setSelectedVacationRequest(vacationRequest)
              }}
              mode="team-member"
              onContextMenu={(e, vacationRequest) => {
                if (
                  !isVacationRequestApproved(vacationRequest) &&
                  !isVacationRequestPending(vacationRequest)
                ) {
                  return
                }
                cmRef?.current.show(e)
                setSelectedVacationRequest(vacationRequest)
              }}
              loading={
                submittingCancel &&
                vacationRequest.id === selectedVacationRequest?.id
              }
            />
          )
        })}
      </div>
    ))
  }

  const hasFilter =
    TEAM_VACATION_REQUESTS_FILTER_OPTIONS.past_requests !==
      selectedFilter.past_requests ||
    TEAM_VACATION_REQUESTS_FILTER_OPTIONS.rejected_requests !==
      selectedFilter.rejected_requests

  const emptyResultsTemplate = ({ hasFilters }) => {
    const emptyMessageTitle =
      hasFilters ? "Sin resultados" : "Nada por aquí (todavía)"
    const emptyMessageContent =
      hasFilters ?
        "No hay solicitudes encontradas con tus filtros, prueba con otros."
      : "No hay solicitudes de vacaciones en el rango seleccionado."
    return (
      <div className="empty-data-team-vacation-requests text-center flex flex-column justify-content-center w-full">
        <i className="pi pi-folder-open"></i>
        <h3 className="title font-cairo">{emptyMessageTitle}</h3>
        <p className="message">{emptyMessageContent}</p>
      </div>
    )
  }

  const renderEmptyState = () => {
    if (loading) {
      return Array.from({ length: 7 }).map((_, index) => (
        <VacationRequestCardSkeleton key={index} />
      ))
    }
    if (getFilteredRequests(vacationRequests).length === 0) {
      return emptyResultsTemplate({
        hasFilters: hasFilter,
      })
    }
  }
  return (
    <Card className="team-member-vacations-requests-container shadow-2">
      <TeamMemberVacationsHeader
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        vacationRequests={vacationRequests}
      />
      <div ref={listContainerRef} className="requests-list my-3">
        {renderVacationRequests()}
        {renderEmptyState()}
        <FloatingButton
          visible={showSeeMore && vacationRequests.length > 0}
          onClick={() => {
            listContainerRef.current.scrollTo({
              top: listContainerRef.current.scrollHeight,
              behavior: "smooth",
            })
          }}
        />
      </div>
      <TeamMemberVacationRequestsContextMenu
        ref={cmRef}
        vacationRequest={selectedVacationRequest}
        setVisibleModal={setVisibleModal}
        triggerRefresh={triggerRefresh}
        submittingCancel={submittingCancel}
        setSubmittingCancel={setSubmittingCancel}
      />
    </Card>
  )
}

export default TeamMemberVacationsRequests

TeamMemberVacationsRequests.propTypes = {
  selectedPeriod: PropTypes.array.isRequired,
  setVisibleModal: PropTypes.func.isRequired,
  setSelectedVacationRequest: PropTypes.func.isRequired,
  refresh: PropTypes.bool,
}
